import { ReactNode, useCallback, useState } from 'react';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  PropertyTitleOwnerOutput,
  useCreateTitleOwnerDocumentMutation,
  useSubmitTitleOwnerIncomeMutation,
  useUpdateTitleOwnerDocumentStatusMutation,
  useVerifyTitleOwnerIncomeDocumentsMutation,
} from '@graphql/index';
import { useResizeDocumentForUpload, useUploadFilesToS3 } from '@hooks';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button, ButtonLink } from '@spark-web/button';
import { Checkbox } from '@spark-web/checkbox';
import { Columns } from '@spark-web/columns';
import { Divider } from '@spark-web/divider';
import { Dropzone } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { Heading } from '@spark-web/heading';
import { CheckCircleIcon, DocumentTextIcon, ExclamationIcon, XCircleIcon } from '@spark-web/icon';
import { ContentDialog } from '@spark-web/modal-dialog';
import { RadioCard, RadioGroup } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink, TextLinkButton } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import noaSampleImage from '../../../assets/NoA_sample.png';
import taxReturnSampleImage from '../../../assets/tax_return_sample.png';
import EligibilityStepHeader from './EligibilityStepHeader';

type NoaVerificationErrorCode =
  | 'UNKNOWN_DOCUMENT_TYPE'
  | 'DOCUMENT_TYPE_MISMATCH'
  | 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE'
  | 'NOTICE_OF_ASSESSMENT_NO_YEAR'
  | 'DOCUMENT_UNREADABLE';

type NoaErrorContent = { description?: string };

const NOA_VERIFICATION_ERROR_CONTENT: Record<NoaVerificationErrorCode, NoaErrorContent> = {
  UNKNOWN_DOCUMENT_TYPE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_TYPE_MISMATCH: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE: {
    description: 'Please upload the assessment for either FY24 or FY25.',
  },
  NOTICE_OF_ASSESSMENT_NO_YEAR: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_UNREADABLE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and that any password protections are removed.',
  },
};

type VerifyDeclaredIncomeProps = {
  owners: PropertyTitleOwnerOutput[];
  declaredIncomes: Record<string, string>;
  trackingId: string;
  productName: string;
  applicantName: string;
  onConfirm: () => void;
  onBack: () => void;
};

type OwnerVerificationStatus =
  | 'idle'
  | 'uploading'
  | 'passed'
  | 'failed_once'
  | 'failed_twice'
  | 'failed_final'
  | 'zero_income_skip'
  | 'income_zero_noa';

const INCOME_ZERO_STATUS_CODE = 'INCOME_ZERO';

function formatMoney(amount: string): string {
  return `$${Number(amount).toLocaleString('en-AU')}`;
}

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BACKGROUNDS: Array<'primaryLow' | 'infoLow'> = ['primaryLow', 'infoLow'];

type DocumentExampleCardProps = {
  variant: 'upload' | 'avoid';
  title: string;
  image: string;
  imageAlt: string;
  children: ReactNode;
};

function DocumentExampleCard({ variant, title, image, imageAlt, children }: DocumentExampleCardProps) {
  const isUpload = variant === 'upload';
  return (
    <Box
      flex={1}
      minWidth={0}
      background="surface"
      borderRadius="medium"
      borderWidth="standard"
      border={isUpload ? 'primary' : 'critical'}
      padding="small"
    >
      <Stack gap="small">
        <Box display="flex" alignItems="center" gap="xsmall">
          {isUpload ? <CheckCircleIcon tone="positive" size="xsmall" /> : <XCircleIcon tone="critical" size="xsmall" />}
          <Text size="small" weight="semibold" tone={isUpload ? 'positive' : 'critical'}>
            {title}
          </Text>
        </Box>
        <Text size="small">{children}</Text>
        <Box
          display="flex"
          justifyContent="center"
          background={isUpload ? 'primaryLow' : 'criticalLow'}
          borderRadius="small"
          padding="small"
        >
          <img
            src={image}
            alt={imageAlt}
            className={css({ width: '100%', maxWidth: 229, height: 'auto', display: 'block', borderRadius: 4 })}
          />
        </Box>
      </Stack>
    </Box>
  );
}

const MYGOV_URL = 'https://my.gov.au/';

function NoaHelpModal({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <ContentDialog
      title="Finding your Notice of Assessment"
      isOpen={isOpen}
      onToggle={onToggle}
      size="small"
      css={{ '& header': { paddingTop: 24 } }}
      footer={
        <ButtonLink href={MYGOV_URL} target="_blank" rel="noopener noreferrer">
          Go to myGov
        </ButtonLink>
      }
    >
      <TextList type="number" size="large" gap="medium">
        <Text>
          Sign in to{' '}
          <TextLink href={MYGOV_URL} tone="primaryActive" target="_blank" rel="noopener noreferrer">
            myGov
          </TextLink>{' '}
          and select the Australian Taxation Office link.
        </Text>
        <Text>
          Go to the menu and navigate to:{' '}
          <Text as="span" inline weight="semibold">
            Tax &gt; Lodgments &gt; Income tax
          </Text>
        </Text>
        <Text>
          Select the{' '}
          <Text as="span" inline weight="semibold">
            History
          </Text>{' '}
          tab.
        </Text>
        <Text>
          Click the{' '}
          <Text as="span" inline weight="semibold">
            Notice of Assessment
          </Text>{' '}
          link for the relevant financial year to download and save a PDF.
        </Text>
      </TextList>
    </ContentDialog>
  );
}

type OwnerCardProps = {
  owner: PropertyTitleOwnerOutput;
  declaredIncome: string;
  trackingId: string;
  avatarBackground: 'primaryLow' | 'infoLow';
  isApplicant: boolean;
  onConsentChange: (ownerId: string, consented: boolean) => void;
  onStatusChange: (ownerId: string, status: OwnerVerificationStatus) => void;
  onPensionerChange: (ownerId: string, isPensioner: boolean | null) => void;
};

function OwnerVerificationCard({
  owner,
  declaredIncome,
  trackingId,
  avatarBackground,
  isApplicant,
  onConsentChange,
  onStatusChange,
  onPensionerChange,
}: OwnerCardProps) {
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<OwnerVerificationStatus>('idle');
  const [verifyErrorCode, setVerifyErrorCode] = useState<NoaVerificationErrorCode | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [tooManyFilesError, setTooManyFilesError] = useState(false);
  const [zeroIncomeConfirmed, setZeroIncomeConfirmed] = useState(false);
  const [pensionerAnswer, setPensionerAnswer] = useState<boolean | null>(null);
  // Filename of the NOA that legibly reported $0 income, shown alongside the pensioner prompt.
  const [zeroIncomeFileName, setZeroIncomeFileName] = useState<string | null>(null);
  // Tracks files already create+uploaded+status'd so a retry skips them instead of duplicating.
  const [uploadedDocIds, setUploadedDocIds] = useState<Map<File, string>>(new Map());
  // Counts every non-passing verify (unreadable OR a re-uploaded $0 NOA) so all retry
  // paths share one cap — otherwise the $0 -> "No" -> re-upload loop runs unbounded.
  const [attemptCount, setAttemptCount] = useState(0);

  const isZeroIncome = Number(declaredIncome) === 0;

  const { mutateAsync: createDocument } = useCreateTitleOwnerDocumentMutation({ useErrorBoundary: false });
  const { mutateAsync: uploadToS3 } = useUploadFilesToS3();
  const resizeDocumentForUpload = useResizeDocumentForUpload();
  const { mutateAsync: updateDocumentStatus } = useUpdateTitleOwnerDocumentStatusMutation({ useErrorBoundary: false });
  const { mutateAsync: verifyDocuments } = useVerifyTitleOwnerIncomeDocumentsMutation({ useErrorBoundary: false });

  const updateStatus = (next: OwnerVerificationStatus) => {
    setStatus(next);
    onStatusChange(owner.id, next);
  };

  const MAX_FILES = 2;
  // Total verification attempts (any non-passing result) allowed before manual review.
  const MAX_ATTEMPTS = 3;

  const handleFileDrop = (event: { target: { value: unknown } }) => {
    const value = event?.target?.value;
    const files = Array.isArray(value) ? (value as File[]) : [];
    setUploadError(undefined);
    // A fresh selection starts a new upload — forget any previously-uploaded docs.
    setUploadedDocIds(new Map());
    if (files.length > MAX_FILES) {
      setTooManyFilesError(true);
      setSelectedFiles([]);
      return;
    }
    setTooManyFilesError(false);
    setSelectedFiles(files);
  };

  const handleUploadAndVerify = async () => {
    if (selectedFiles.length === 0) return;

    const previousStatus = status;
    updateStatus('uploading');
    setUploadError(undefined);
    setVerifyErrorCode(null);
    // A fresh upload attempt clears any prior pensioner answer (e.g. re-uploading after "No").
    setPensionerAnswer(null);
    onPensionerChange(owner.id, null);

    try {
      // Carry forward files uploaded on a previous attempt so a retry resumes the rest.
      const currentUploaded = new Map(uploadedDocIds);
      let failedCount = 0;

      for (const file of selectedFiles) {
        if (currentUploaded.has(file)) continue;

        try {
          // Large files are compressed by the backend before upload; small files pass through.
          const uploadFile = await resizeDocumentForUpload(file);

          const createResult = await createDocument({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              filename: file.name,
              fileSize: uploadFile.size,
            },
          });

          const { document, url, fieldsJson } = createResult.createTitleOwnerDocument;

          if (!url || !fieldsJson) {
            throw new Error('Failed to get presigned upload URL');
          }

          const uploadMetaData = JSON.parse(fieldsJson);
          const formData = new FormData();
          Object.keys(uploadMetaData).forEach(key => formData.append(key, uploadMetaData[key]));
          formData.append('file', uploadFile);

          await uploadToS3({ bucketUrl: url, file: formData });

          await updateDocumentStatus({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              documentId: document.id,
            },
          });

          currentUploaded.set(file, document.id);
        } catch {
          failedCount++;
        }
      }

      setUploadedDocIds(currentUploaded);

      if (failedCount > 0) {
        const succeeded = currentUploaded.size;
        const total = selectedFiles.length;

        if (succeeded > 0) {
          setUploadError('Few files failed to upload. Please try again.');
          datadogRum.addAction('LandOwner Document Partial Upload Error', { trackingId, succeeded, total });
        } else {
          setUploadError('Something went wrong uploading your document. Please try again.');
          datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
        }

        updateStatus(previousStatus);
        return;
      }

      const documentIds = selectedFiles.map(file => currentUploaded.get(file) as string);

      const verifyResult = await verifyDocuments({
        input: {
          applicationId: trackingId,
          titleSearchOwnerId: owner.id,
          documentIds,
        },
      });

      const { validation, statusCode } = verifyResult.verifyTitleOwnerIncomeDocuments;

      if (validation === 'Passed') {
        updateStatus('passed');
        setVerifyErrorCode(null);
        datadogRum.addAction('LandOwner Document Verified', { trackingId });
      } else if (statusCode === INCOME_ZERO_STATUS_CODE) {
        // The NOA legibly reports $0 income — don't treat it as unreadable. Prompt for
        // pensioner status. Both answers are terminal (no re-upload), so this never loops
        // and is deliberately not counted toward the unreadable retry cap.
        setVerifyErrorCode(null);
        setZeroIncomeFileName(selectedFiles[0]?.name ?? null);
        updateStatus('income_zero_noa');
        datadogRum.addAction('LandOwner Document Zero Income', { trackingId });
      } else {
        // Unreadable / mismatched document — retryable, capped at MAX_ATTEMPTS.
        const nextAttempt = attemptCount + 1;
        setAttemptCount(nextAttempt);

        const mapped = statusCode ? NOA_VERIFICATION_ERROR_CONTENT[statusCode as NoaVerificationErrorCode] : undefined;
        const resolvedCode = mapped ? (statusCode as NoaVerificationErrorCode) : 'DOCUMENT_UNREADABLE';
        setVerifyErrorCode(resolvedCode);

        if (nextAttempt >= MAX_ATTEMPTS) {
          updateStatus('failed_final');
          datadogRum.addAction('LandOwner Document Verify Final Failed', {
            trackingId,
            errorCode: resolvedCode,
          });
        } else {
          updateStatus(nextAttempt === 1 ? 'failed_once' : 'failed_twice');
        }
      }

      setSelectedFiles([]);
      setUploadedDocIds(new Map());
    } catch {
      setUploadError('Something went wrong uploading your document. Please try again.');
      datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
      updateStatus(previousStatus);
    }
  };

  const isUploading = status === 'uploading';
  const hasFinallyFailed = status === 'failed_final';
  const hasPassed = status === 'passed';
  const isZeroIncomeNoa = status === 'income_zero_noa';
  // Once a $0-NOA owner is prompted for pensioner status, the upload step is done: "Yes"
  // verifies via consent and "No" continues unverified — neither re-opens the dropzone.
  const showUploadSection = !isZeroIncomeNoa;
  const isUploadDisabled = hasPassed || hasFinallyFailed || isUploading || isZeroIncomeNoa;
  const disabledDropzoneClassName = css({ pointerEvents: 'none', opacity: 0.5 });

  return (
    <Box background="surfaceLow" borderRadius="medium" padding="large" borderWidth="standard" border="standard">
      <Stack gap="large">
        <Box
          display="flex"
          flexDirection={{ mobile: 'column', tablet: 'row' }}
          justifyContent={{ mobile: 'start', tablet: 'spaceBetween' }}
          alignItems={{ mobile: 'start', tablet: 'center' }}
          gap={{ mobile: 'large', tablet: 'medium' }}
        >
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            alignItems={{ mobile: 'start', tablet: 'center' }}
            gap={{ mobile: 'xsmall', tablet: 'medium' }}
          >
            <Box
              borderRadius="full"
              background={avatarBackground}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ width: 40, height: 40 }}
            >
              <Text weight="semibold" size="small">
                {getInitials(owner.fullName)}
              </Text>
            </Box>
            <Text weight="semibold">{owner.fullName}</Text>
          </Box>
          <Text tone="muted" size="small">
            Declared: {formatMoney(declaredIncome)}
          </Text>
        </Box>

        {hasPassed && (
          <Box
            display="flex"
            alignItems="center"
            gap="small"
            background="positiveLow"
            padding="medium"
            borderRadius="medium"
          >
            <CheckCircleIcon tone="positive" size="small" />
            <Text size="small">Notice of assessment is verified.</Text>
          </Box>
        )}

        {hasFinallyFailed && (
          <Box
            display="flex"
            alignItems="start"
            gap="small"
            background="cautionLow"
            padding="medium"
            borderRadius="medium"
          >
            <Box flexShrink={0} paddingTop="xxsmall">
              <ExclamationIcon tone="caution" size="small" />
            </Box>
            <Stack gap="xsmall">
              <Text size="small">
                We weren&apos;t able to read your Notice of Assessment. You can continue through to the application and
                our team will contact you to manually check your eligibility.
              </Text>
            </Stack>
          </Box>
        )}

        {isZeroIncomeNoa && (
          <Stack gap="medium">
            {zeroIncomeFileName && (
              <Box
                display="flex"
                alignItems="center"
                gap="small"
                padding="medium"
                borderRadius="medium"
                borderWidth="standard"
                border="standard"
              >
                <DocumentTextIcon tone="muted" size="small" />
                <Text size="small">{zeroIncomeFileName}</Text>
              </Box>
            )}

            <Stack gap="medium" marginY="large">
              <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                <Text size="small" tone="muted">
                  Income read from document
                </Text>
                <Text size="small" weight="semibold">
                  $0
                </Text>
              </Box>
              <Divider />
            </Stack>

            <Stack gap="small" marginY="small">
              <Text weight="semibold" size="small">
                Are you a pensioner?
              </Text>
              <RadioGroup<'yes' | 'no' | ''>
                aria-label="Are you a pensioner?"
                value={pensionerAnswer === true ? 'yes' : pensionerAnswer === false ? 'no' : ''}
                onChange={value => {
                  const answer = value === 'yes';
                  setPensionerAnswer(answer);
                  onPensionerChange(owner.id, answer);
                }}
              >
                <Columns gap="large" collapseBelow="tablet">
                  <RadioCard value="yes">Yes</RadioCard>
                  <RadioCard value="no">No</RadioCard>
                </Columns>
              </RadioGroup>
            </Stack>

            {pensionerAnswer === true && (
              <Checkbox
                checked={consent}
                onChange={event => {
                  setConsent(event.target.checked);
                  onConsentChange(owner.id, event.target.checked);
                }}
              >
                {isApplicant
                  ? 'I consent to Brighte using this document to verify my income for NSW Government Loan eligibility purposes.'
                  : `I confirm I am authorised to upload documents on behalf of ${owner.fullName}, and that ${
                      owner.fullName.split(' ')[0]
                    } consents to Brighte verifying their income for this application.`}
              </Checkbox>
            )}
          </Stack>
        )}

        {isZeroIncome ? (
          <Stack gap="medium">
            <Checkbox
              checked={zeroIncomeConfirmed}
              onChange={event => {
                const checked = event.target.checked;
                setZeroIncomeConfirmed(checked);
                updateStatus(checked ? 'zero_income_skip' : 'idle');
              }}
            >
              {isApplicant
                ? 'I confirm I have $0 taxable income and do not have a Notice of Assessment.'
                : `I confirm ${owner.fullName} has $0 taxable income and does not have a Notice of Assessment.`}
            </Checkbox>
            {zeroIncomeConfirmed && (
              <Box
                display="flex"
                alignItems="start"
                gap="small"
                background="cautionLow"
                padding="medium"
                borderRadius="medium"
              >
                <Box flexShrink={0} paddingTop="xxsmall">
                  <ExclamationIcon tone="caution" size="small" />
                </Box>
                <Text size="small">
                  We understand you don&apos;t have a Notice of Assessment. You can continue through to the application
                  and our team will contact you to manually check your eligibility.
                </Text>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <Stack gap="medium">
              {verifyErrorCode &&
                !isUploadDisabled &&
                (() => {
                  const errorContent = NOA_VERIFICATION_ERROR_CONTENT[verifyErrorCode];
                  return (
                    <Alert tone="critical">
                      <Stack gap="xsmall">
                        {errorContent.description && <Text size="small">{errorContent.description}</Text>}
                      </Stack>
                    </Alert>
                  );
                })()}

              {uploadError && <Alert tone="critical">{uploadError}</Alert>}

              {tooManyFilesError && (
                <Alert tone="critical" heading="Too many files selected">
                  <Text size="small">
                    You can only upload up to {MAX_FILES} files at once. Please select fewer files and try again.
                  </Text>
                </Alert>
              )}

              {showUploadSection && (
                <Box
                  marginY="large"
                  aria-disabled={isUploadDisabled}
                  className={isUploadDisabled ? disabledDropzoneClassName : undefined}
                >
                  <Field label="Upload Notice of Assessment" disabled={isUploadDisabled}>
                    <Dropzone
                      key={isUploadDisabled ? 'disabled' : 'active'}
                      accept={ACCEPTED_FILE_TYPES}
                      onChange={isUploadDisabled ? undefined : handleFileDrop}
                      maxFileSizeKb={Math.floor(MAX_FILE_SIZE_BYTES / 1024)}
                      maxFiles={0}
                      multiple={true}
                    />
                    <Text size="xsmall" tone="muted">
                      You can upload up to {MAX_FILES} files at once.
                    </Text>
                  </Field>
                </Box>
              )}

              {selectedFiles.length > 0 && !hasPassed && !hasFinallyFailed && (
                <Box display="flex" justifyContent="center">
                  <Button onClick={handleUploadAndVerify} loading={isUploading} disabled={isUploading}>
                    Upload and verify
                  </Button>
                </Box>
              )}
            </Stack>

            {hasPassed && (
              <Checkbox
                checked={consent}
                onChange={event => {
                  setConsent(event.target.checked);
                  onConsentChange(owner.id, event.target.checked);
                }}
              >
                {isApplicant
                  ? 'I consent to Brighte using this document to verify my income for NSW Government Loan eligibility purposes.'
                  : `I confirm I am authorised to upload documents on behalf of ${owner.fullName}, and that ${owner.fullName.split(' ')[0]} consents to Brighte verifying their income for this application.`}
              </Checkbox>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

function VerifyDeclaredIncome({
  owners,
  declaredIncomes,
  trackingId,
  productName,
  applicantName,
  onConfirm,
  onBack,
}: VerifyDeclaredIncomeProps) {
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [ownerStatuses, setOwnerStatuses] = useState<Record<string, OwnerVerificationStatus>>({});
  const [pensionerAnswers, setPensionerAnswers] = useState<Record<string, boolean | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNoaHelpOpen, setIsNoaHelpOpen] = useState(false);

  const { mutateAsync: submitIncome } = useSubmitTitleOwnerIncomeMutation({ useErrorBoundary: false });

  const handleConsentChange = useCallback((ownerId: string, consented: boolean) => {
    setConsents(prev => ({ ...prev, [ownerId]: consented }));
  }, []);

  const handleStatusChange = useCallback((ownerId: string, ownerStatus: OwnerVerificationStatus) => {
    setOwnerStatuses(prev => ({ ...prev, [ownerId]: ownerStatus }));
  }, []);

  const handlePensionerChange = useCallback((ownerId: string, isPensioner: boolean | null) => {
    setPensionerAnswers(prev => ({ ...prev, [ownerId]: isPensioner }));
  }, []);

  const anyOwnerFinallyFailed = owners.some(owner => ownerStatuses[owner.id] === 'failed_final');

  const allConsented =
    anyOwnerFinallyFailed ||
    owners.every(
      owner =>
        ownerStatuses[owner.id] === 'failed_final' ||
        ownerStatuses[owner.id] === 'zero_income_skip' ||
        // "No" continues as unverified without a consent checkbox; "Yes" requires consent (below).
        (ownerStatuses[owner.id] === 'income_zero_noa' && pensionerAnswers[owner.id] === false) ||
        consents[owner.id] === true,
    );
  const allOwnersDone =
    anyOwnerFinallyFailed ||
    owners.every(owner => {
      const ownerStatus = ownerStatuses[owner.id];
      return (
        ownerStatus === 'passed' ||
        ownerStatus === 'failed_final' ||
        ownerStatus === 'zero_income_skip' ||
        // Either answer ("Yes" + consent, or "No" continue-unverified) is a terminal state.
        (ownerStatus === 'income_zero_noa' && typeof pensionerAnswers[owner.id] === 'boolean')
      );
    });
  const canContinue = allConsented && allOwnersDone;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await submitIncome({
        input: {
          applicationId: trackingId,
          owners: owners.map(owner => {
            const annualIncome = Number(declaredIncomes[owner.id] ?? 0);
            const monthlyIncome = annualIncome / 12;
            const ownerStatus = ownerStatuses[owner.id];
            const isZeroIncomeNoaOwner = ownerStatus === 'income_zero_noa';
            const pensionerAnswer = pensionerAnswers[owner.id];
            // A confirmed pensioner must NOT send isIncomeVerified:false — the BE
            // treats an explicit false as an override that skips the pensioner
            // short-circuit, so verification could never succeed. Omit the field
            // and let the BE derive it from isPensioner=true.
            const isConfirmedPensioner = isZeroIncomeNoaOwner && pensionerAnswer === true;
            const isUnverified =
              ownerStatus === 'failed_final' ||
              ownerStatus === 'zero_income_skip' ||
              (isZeroIncomeNoaOwner && !isConfirmedPensioner) ||
              // Confirmed pensioners stay exempt even when another owner failed.
              (anyOwnerFinallyFailed && ownerStatus !== 'passed' && !isConfirmedPensioner);
            return {
              id: owner.id,
              declaredIncome: monthlyIncome,
              ...(isUnverified ? { isIncomeVerified: false } : {}),
              ...(isZeroIncomeNoaOwner && typeof pensionerAnswer === 'boolean' ? { isPensioner: pensionerAnswer } : {}),
            };
          }),
        },
      });
      onConfirm();
    } catch {
      datadogRum.addError(new Error('HESL income submission failed'), {
        trackingId,
        ownerCount: owners.length,
      });
      datadogRum.addAction('LandOwner Income Submit Error', { trackingId, ownerCount: owners.length });
      onConfirm();
    }
    setIsSubmitting(false);
  };

  return (
    <Stack padding="xlarge" gap="xlarge">
      <EligibilityStepHeader currentStep={5} totalSteps={5} productName={productName} />

      <Stack gap="xlarge">
        <Box className={css({ lineHeight: '1.3em' })}>
          <Heading level="1">Upload documents to verify your income</Heading>
        </Box>

        <Box background="surface" borderRadius="medium" padding="large" borderWidth="standard" border="standard">
          <Stack gap="medium">
            <Text size="standard">
              Upload a{' '}
              <Text as="span" weight="semibold" inline>
                Notice of Assessment (FY24 or FY25)
              </Text>{' '}
              for each person on title to verify your income.
            </Text>

            <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }} gap="small">
              <DocumentExampleCard
                variant="upload"
                title="Upload this"
                image={noaSampleImage}
                imageAlt="Example Notice of Assessment document"
              >
                Titled as{' '}
                <Text as="span" weight="semibold" inline size="small">
                  &ldquo;Notice of Assessment&rdquo;
                </Text>{' '}
                - the ATO sends it after you lodge.
              </DocumentExampleCard>
              <DocumentExampleCard
                variant="avoid"
                title="Not this"
                image={taxReturnSampleImage}
                imageAlt="Example tax return document"
              >
                Titled as{' '}
                <Text as="span" weight="semibold" inline size="small">
                  &ldquo;Tax return&rdquo;
                </Text>{' '}
                - this is what you lodge, not the assessment.
              </DocumentExampleCard>
            </Box>

            <Divider />

            <Text>
              <TextLinkButton onClick={() => setIsNoaHelpOpen(true)} tone="primaryActive" weight="semibold">
                Need help finding your Notice of Assessment?
              </TextLinkButton>
            </Text>
          </Stack>
        </Box>

        <NoaHelpModal isOpen={isNoaHelpOpen} onToggle={() => setIsNoaHelpOpen(false)} />

        <Box className={css({ lineHeight: '1.3em' })}>
          <Heading level="2">Your documents</Heading>
        </Box>

        {owners.map((owner, index) => (
          <OwnerVerificationCard
            key={owner.id}
            owner={owner}
            declaredIncome={declaredIncomes[owner.id] ?? '0'}
            trackingId={trackingId}
            avatarBackground={AVATAR_BACKGROUNDS[index] ?? 'infoLow'}
            isApplicant={owner.fullName.trim().toLowerCase() === applicantName.trim().toLowerCase()}
            onConsentChange={handleConsentChange}
            onStatusChange={handleStatusChange}
            onPensionerChange={handlePensionerChange}
          />
        ))}

        <Box>
          <Accordion type="multiple">
            <AccordionItem value="q0" label="Why do I need to upload my Notice of Assessment?" level="4">
              <Text>
                The NSW Government requires income verification for this program. Your Notice of Assessment is an
                official ATO document that confirms your taxable income. We compare it against the figure you declared
                in the previous step to verify your household qualifies.
              </Text>
            </AccordionItem>
            <AccordionItem value="q1" label="I don't have the documents needed right now" level="4">
              <Text>
                No problem, your progress has been saved. You can return to this step once you have your documents ready
                by clicking the link in your invite email.
              </Text>
            </AccordionItem>
            <AccordionItem value="q2" label="Where can I find my Notice of Assessment?" level="4">
              <Text>
                You can download a copy of your Notice of Assessment in{' '}
                <TextLink href="https://my.gov.au/" tone="primary" target="_blank" rel="noopener noreferrer">
                  MyGov
                </TextLink>
                .
              </Text>
            </AccordionItem>
          </Accordion>
        </Box>

        <Button tone="primary" onClick={handleConfirm} disabled={!canContinue} loading={isSubmitting}>
          Continue to application
        </Button>

        <Box alignSelf="start">
          <Button prominence="none" tone="neutral" onClick={onBack}>
            ← Back
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

export default VerifyDeclaredIncome;
